import type { PaletteOptions } from '@mui/material/styles';
import { color } from './tokens/primitives';
import { semantic } from './tokens/semantic';

/**
 * MUI palette mapping. Sources every value from `tokens/primitives` or
 * `tokens/semantic` — no inline hex.
 *
 * Mapping notes:
 * - `primary.main` is `vayuBlue[950]` (#07105F) — the action navy that
 *   Figma's `Button-primary-idle` aliases to. `vayuBlue[500]` (#2953FF)
 *   is the bright focus accent (not `primary.main`).
 * - `text.*` and `divider` are sourced from the semantic layer.
 * - `background.default` = page bg (`semantic.bg.primary`),
 *   `background.paper` = card bg (`semantic.bg.cardPrimary`).
 * - `neutral` and `purple` are extended semantic colors (see module
 *   augmentation below). `neutral` aliases the dark text ramp so
 *   `color="neutral"` on a tinted surface picks the same value Figma's
 *   `Content/Primary` token resolves to. `purple` exposes the tertiary
 *   accent ramp (used by Insights agent and similar decorative icons).
 *
 * Light mode only. No dark variant in this phase.
 */
declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
    purple: Palette['primary'];
  }
  interface PaletteOptions {
    neutral?: PaletteOptions['primary'];
    purple?: PaletteOptions['primary'];
  }
}
export const palette: PaletteOptions = {
  mode: 'light',

  primary: {
    main: color.vayuBlue[950],
    light: color.vayuBlue[500],
    dark: color.vayuBlue[900],
    contrastText: color.base.white,
  },

  secondary: {
    main: color.turquoise[500],
    light: color.turquoise[300],
    dark: color.turquoise[700],
    contrastText: color.neutrals[900],
  },

  error: {
    main: color.alert[900],
    light: color.alert[500],
    dark: color.alert[950],
    contrastText: color.base.white,
  },

  warning: {
    main: color.orange[500],
    light: color.orange[300],
    dark: color.orange[700],
    contrastText: color.neutrals[900],
  },

  info: {
    main: color.vayuBlue[500],
    light: color.vayuBlue[300],
    dark: color.vayuBlue[700],
    contrastText: color.base.white,
  },

  success: {
    main: color.success[600],
    light: color.success[400],
    dark: color.success[800],
    contrastText: color.base.white,
  },

  neutral: {
    main: color.neutrals[900],
    light: color.neutrals[600],
    dark: color.neutrals[950],
    contrastText: color.base.white,
  },

  purple: {
    main: color.purple[600],
    light: color.purple[400],
    dark: color.purple[800],
    contrastText: color.base.white,
  },

  text: {
    primary: semantic.text.primary,
    secondary: semantic.text.secondary,
    disabled: color.neutrals[400],
  },

  background: {
    default: semantic.bg.primary,
    paper: semantic.bg.cardPrimary,
  },

  divider: semantic.divider.default,

  action: {
    hover: semantic.dropdown.menuItem.hover,
    selected: semantic.dropdown.menuItem.focused,
    disabled: color.neutrals[300],
    disabledBackground: color.neutrals[100],
    focus: color.vayuBlue[500],
  },
};
